import styled from '@emotion/styled';
import { FormControl, Typography } from '@mui/material';
import { useState } from 'react';
import { CalenderIcon } from '@/icons/CalenderIcon';
import dayjs from 'dayjs';
import { TEXT_SIZE } from '@/typography/Typography';
import DatePicker from 'react-datepicker';
import { PrimaryButton, SecondaryButtonRegular } from './Button';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useFormatting } from '@/utils/formatting';

const DropDown = styled('div')`
    margin: 0 auto;
    z-index: 10000;
    min-width: auto;
    position: relative;
`;

const DropDownBtn = styled('div')`
    cursor: pointer;
    background: white;
    display: flex;
    align-items: center;
    padding: 10px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    color: #777;
    font-weight: 500;
    padding: 8px 14px;
`;

const DropDownContent = styled('div')`
    position: absolute;
    left: 10;
    margin-left: 8px;
    //min-width: -webkit-fill-available;
    box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.07);
    background: white;
    border-radius: 10px;
`;

const DropDownItem = styled('div')`
    display: block;
    padding: 8px;
    margin: 10px;
    line-height: 16px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;

    &:hover {
        background: #eff8ff;
    }
`;

const DatePickerFooter = styled('div')`
    display: flex;
    justify-content: space-between;
    padding: 5px;
    border-left: 1px solid #f0f0f0;
`;

const DateInput = styled('input')`
    border-radius: 4px;
    border: 1px solid #d0d5dd;
    width: 90px;
    height: 30px;
    text-align: center;
    margin: 5px;
`;

const DatePickerButtons = styled('div')`
    display: flex;
    align-items: center;
    width: 150px;
    justify-content: space-evenly;
`;

interface DateRangeDropdownProps {
    startDate?: Date;
    endDate?: Date;
    timeFilter: string;
    showDatePicker: boolean;
    filterOptions: { name: string; value: string }[];
    setTimeFilter: (timeFilter: string) => void;
    filtereDateData: (timeFilter: string) => void;
    onChange?: (dates: any) => void;
    handleClose?: () => void;
    handleApply?: () => void;
    onMonthChange?: (date: Date) => void;
}

export const DateRangeDropdown = ({
    startDate,
    endDate,
    timeFilter,
    setTimeFilter,
    filterOptions,
    filtereDateData,
    handleClose,
    onChange,
    handleApply: handleApplyParent,
    onMonthChange
}: DateRangeDropdownProps) => {
    const [isActive, setIsActive] = useState(false);

    const { t } = useTranslation();
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showMonthPicker, setShowMonthPicker] = useState(false);

    const handleMouseEnter = (option: any) => {
        setShowDatePicker(option === 'customDate');
        setShowMonthPicker(option === 'customMonth');
    };

    const handleApply = () => {
        setIsActive(false);
        setShowDatePicker(false);
        handleApplyParent?.();
    };

    const handleMonthApply = (date: Date) => {
        setIsActive(false);
        setShowMonthPicker(false);
        onMonthChange?.(date);
    };

    const { formatDate } = useFormatting();

    const renderDatePicker = () => {
        return (
            <div style={{ display: 'block' }}>
                <DatePicker
                    calendarStartDay={1}
                    selected={startDate ? new Date(startDate) : undefined}
                    onChange={onChange}
                    startDate={startDate ? new Date(startDate) : undefined}
                    endDate={endDate ? new Date(endDate) : undefined}
                    monthsShown={2}
                    selectsRange
                    inline
                    shouldCloseOnSelect={false}
                />

                <DatePickerFooter>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <DateInput
                            value={
                                startDate
                                    ? dayjs(startDate).format('MMMM D, YYYY')
                                    : ''
                            }
                            readOnly
                        />
                        <p> - </p>
                        <DateInput
                            value={
                                endDate
                                    ? dayjs(endDate).format('MMMM D, YYYY')
                                    : ''
                            }
                            readOnly
                        />
                    </div>
                    <DatePickerButtons>
                        <SecondaryButtonRegular onClick={handleClose}>
                            {t('close')}
                        </SecondaryButtonRegular>
                        <PrimaryButton onClick={handleApply}>
                            {t('apply')}
                        </PrimaryButton>
                    </DatePickerButtons>
                </DatePickerFooter>
            </div>
        );
    };

    const renderMonthPicker = () => {
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StaticDatePicker
                    openTo="month"
                    views={['year', 'month']}
                    slotProps={{
                        toolbar: { hidden: true },
                        actionBar: { hidden: true, actions: [] },
                        layout: {
                            sx: {
                                '& .MuiDateCalendar-root': {
                                    height: 'auto'
                                }
                            }
                        }
                    }}
                    onMonthChange={handleMonthApply}
                    value={dayjs() as any}
                />
            </LocalizationProvider>
        );
    };

    const getLabel = () => {
        if (timeFilter == 'customDate' && startDate && endDate) {
            return `${
                startDate ? dayjs(startDate).format('MMMM D, YYYY') : ''
            } - ${endDate ? dayjs(endDate).format('MMMM D, YYYY') : ''}`;
        }

        if (timeFilter == 'customMonth' && startDate) {
            return dayjs(startDate).format('MMMM YYYY');
        }

        return (
            filterOptions.find((option) => option.value === timeFilter)?.name ||
            ''
        );
    };

    return (
        <FormControl>
            <DropDown style={{ zIndex: 1 }}>
                <DropDownBtn onClick={(e) => setIsActive(!isActive)}>
                    <CalenderIcon />
                    <Typography
                        marginLeft={'10px'}
                        color={'#344054'}
                        fontWeight={600}
                        fontSize={TEXT_SIZE.MEDIUM_SMALL}
                        lineHeight={'20px'}
                    >
                        {getLabel()}
                    </Typography>
                </DropDownBtn>
                <DropDownContent
                    style={{
                        display: isActive ? 'flex' : 'none'
                    }}
                >
                    <div
                        style={{
                            display: 'block',
                            textWrap: 'nowrap'
                        }}
                    >
                        {filterOptions.map((option) => (
                            <DropDownItem
                                key={option.value}
                                onClick={(e: any) => {
                                    setTimeFilter(option.value as string);
                                    filtereDateData(option.value);
                                    setIsActive(false);

                                    if (option.value === 'customDate') {
                                        setShowDatePicker(true);
                                    }
                                }}
                                onMouseEnter={() =>
                                    handleMouseEnter(option.value)
                                }
                            >
                                <p
                                    style={{
                                        padding: 0,
                                        margin: 0
                                    }}
                                >
                                    {option.name}
                                </p>
                            </DropDownItem>
                        ))}
                    </div>
                    {showDatePicker && renderDatePicker()}
                    {showMonthPicker && renderMonthPicker()}
                </DropDownContent>
            </DropDown>
        </FormControl>
    );
};
